import React, { useState, useMemo, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import SvgIcon from '@material-ui/core/SvgIcon';
import TablePagination from '@material-ui/core/TablePagination';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import { PDFDownloadLink } from '@react-pdf/renderer';

import AddInvoiceDialog from '../AddInvoiceDialog/AddInvoiceDialog';
import Invoice from '../../Test/Invoice';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudDownload as CloudDownloadIcon,
  Money as MoneyIcon,
  Note as NoteIcon,
} from '@material-ui/icons';
import AddExpenseDialog from '../AddExpenseDialog/AddExpenseDialog';
import { getExpensesRequest, getInvoiceRequest } from '../../actions';
import CreateJobPDF from '../CreateJobPDF/CreateJobPDF';
import CreateInvoiceOnlyPDF from '../PDFGenerator/CreateInvoiceOnlyPDF';
import PdfPrimaryDownloadButton from '../PdfPrimaryDownloadButton/PdfPrimaryDownloadButton';
import SearchInput from '../SearchInput/SearchInput';
import {
  TRACKING_STAGE_ORDERED_KEYS,
  TRACKING_STAGE_LABELS,
} from '../../constants/jobTrackingStages';

function rowStageKey(row) {
  if (row.trackingStage && TRACKING_STAGE_ORDERED_KEYS.includes(row.trackingStage)) {
    return row.trackingStage;
  }
  if (row.status && TRACKING_STAGE_ORDERED_KEYS.includes(String(row.status).trim())) {
    return String(row.status).trim();
  }
  return 'job_created';
}

/** Primary GCN for API routes (car-only jobs may only have carGcnno set). */
function rowPrimaryGcn(row) {
  if (row == null) return undefined;
  if (row.gcnno != null && row.gcnno !== '') return row.gcnno;
  if (row.carGcnno != null && row.carGcnno !== '') return row.carGcnno;
  return row.gcnno;
}

const StyledTableCell = withStyles((theme) => ({
  head: {
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    color: '#f1f5f9',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    borderBottom: '2px solid #DA2E09',
    padding: '14px 12px',
    whiteSpace: 'nowrap',
  },
  body: {
    fontSize: 14,
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    padding: '10px 12px',
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    transition: 'background 0.15s',
    '&:nth-of-type(odd)': {
      backgroundColor: 'rgba(248, 249, 252, 0.8)',
    },
    '&:hover': {
      backgroundColor: 'rgba(218, 46, 9, 0.04) !important',
    },
  },
}))(TableRow);

function createData(consignor, consignee, contact, email, status, type, date) {
  return { consignor, consignee, contact, email, status, type, date };
}

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 1040,
  },
  searchBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  searchField: {
    minWidth: 260,
    maxWidth: 380,
    flex: '1 1 260px',
  },
  tableScroller: {
    overflowX: 'auto',
    width: '100%',
    borderRadius: 12,
    boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
    border: '1px solid rgba(0,0,0,0.07)',
    overflow: 'hidden',
  },
  actionBtn: {
    color: '#64748b',
    padding: 6,
    borderRadius: 8,
    '&:hover': {
      backgroundColor: 'rgba(218, 46, 9, 0.08)',
      color: '#DA2E09',
    },
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  highlightedRow: {
    backgroundColor: 'rgba(76, 175, 80, 0.10) !important',
    transition: 'background-color 0.3s ease',
  },
}));

const JOB_SEARCH_FIELDS = ['gcnno', 'carGcnno', 'consignorF', 'consignorL', 'consigneeF', 'consigneeL', 'email', 'contact'];

function jobMatchesQuickSearch(job, queryLower) {
  if (!queryLower) return true;
  for (let i = 0; i < JOB_SEARCH_FIELDS.length; i += 1) {
    const field = JOB_SEARCH_FIELDS[i];
    const v = job[field];
    if (v != null && String(v).toLowerCase().includes(queryLower)) {
      return true;
    }
  }
  return false;
}

const AllJobsTable = ({
  data,
  onDeleteJob,
  onUpdateJobTracking,
  onAddInvoiceClick,
  match,
  onEditJobClick,
  isLoading,
  onAddExpenseClick,
  highlightedJobNumber,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const [selectedGcnNo, updateSelectedGcnNo] = useState();
  const [selectedToDownloadGcnNo, updateSelectedToDownloadGcnNo] = useState();
  const [selectedInoiceDownloadGcnNo, updateSelectedInoiceDownloadGcnNo] = useState();
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [openDownload, setOpenDownload] = useState(false);
  const [openDownloadInvoice, setOpenDownloadInvoice] = useState(false);
  const [openInvoice, setOpenInvoice] = useState(false);
  const [openExpense, setOpenExpense] = useState(false);
  const [selectedInvoiceStatus, updateSelectedInvoiceStatus] = useState();
  const [selectedExpenseStatus, updateSelectedExpenseStatus] = useState();
  const [searchQuery, setSearchQuery] = useState('');
  const [invoiceData, setInvoiceData] = useState([]);

  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    setSearchQuery(q != null ? q : '');
  }, [location.search]);


  const [noteDialog, setNoteDialog] = useState({ open: false, gcnno: null, draft: '' });

  const { job, allJobs } = useSelector((state) => state.Job);

  const handleClickOpen = (gsnNo) => {
    updateSelectedGcnNo(gsnNo);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseDownload = () => {
    setOpenDownload(false);
  };

  const handleCloseDownloadInvoice = () => {
    setOpenDownloadInvoice(false);
  };

  const handleOpenDownload = (gcnNo) => {
    updateSelectedToDownloadGcnNo(gcnNo);
    setOpenDownload(true);
  };

  const handleOpenDownloadInvoice = (gcnNo) => {
    updateSelectedInoiceDownloadGcnNo(gcnNo);
    setOpenDownloadInvoice(true);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'desc';
    setOrder(isAsc ? 'asc' : 'desc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = () => {
    onDeleteJob(selectedGcnNo);
    setOpen(false);
  };

  const debug = (val) => {
    console.log(val);
  };

  const handleOpenInvoice = (gcnno, isAdded) => {
    updateSelectedGcnNo(gcnno);
    updateSelectedInvoiceStatus(isAdded);
    setOpenInvoice(true);
  };

  const handleAddInvoice = (data) => {
    console.log("Invoice data", data)
    onAddInvoiceClick(data, selectedGcnNo, data.billno, data.invoiceDate, selectedInvoiceStatus);
    setOpenInvoice(false);
  };

  const handleCancleInvoice = () => {
    setOpenInvoice(false);
  };

  const handleOpenExpenses = (gcnno, isAdded) => {
    updateSelectedGcnNo(gcnno);
    updateSelectedExpenseStatus(isAdded);
    setOpenExpense(true);
  };

  const handleAddExpenses = (data) => {
    onAddExpenseClick(data, selectedGcnNo, selectedExpenseStatus);
    setOpenExpense(false);
  };

  const handleCancleExpenses = () => {
    setOpenExpense(false);
  };

  const selectedJob = useMemo(() => {
    return allJobs.find((j) => j.gcnno === selectedToDownloadGcnNo || j.carGcnno === selectedToDownloadGcnNo);
  }, [allJobs, selectedToDownloadGcnNo]);

  const selectedInvoice = useMemo(() => {
    return allJobs.find((j) => j.gcnno === selectedInoiceDownloadGcnNo || j.carGcnno === selectedInoiceDownloadGcnNo);
  }, [allJobs, selectedInoiceDownloadGcnNo]);

  const headCells = [
    { id: 'gcnno', numeric: false, label: 'GCN No.' },
    { id: 'consignor', numeric: false, label: 'Consignor' },
    { id: 'view', numeric: false, label: 'View' },
    { id: 'update', numeric: false, label: 'Update' },
    { id: 'delete', numeric: false, label: 'Delete' },
    { id: 'download', numeric: false, label: 'Download' },
    { id: 'addInvoice', numeric: false, label: 'Add Invoice' },
    { id: 'addExpense', numeric: false, label: 'Add Expense' },
    { id: 'downloadInvoice', numeric: false, label: 'Download Invoice' },
    { id: 'date', numeric: false, label: 'Date' },
    { id: 'trackingStage', numeric: false, label: 'Stage' },
    { id: 'type', numeric: false, label: 'Type' },
    { id: 'contact', numeric: false, label: 'Contact' },
    { id: 'email', numeric: false, label: 'Email' },
  ];

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  function parseDateDMY(str) {
    if (!str) return 0;
    const parts = String(str).split('/');
    if (parts.length === 3) {
      // DD/MM/YYYY → comparable timestamp
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime() || 0;
    }
    return new Date(str).getTime() || 0;
  }

  function descendingComparator(a, b, orderBy) {
    const aVal = orderBy === 'date' ? parseDateDMY(a[orderBy]) : a[orderBy];
    const bVal = orderBy === 'date' ? parseDateDMY(b[orderBy]) : b[orderBy];
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const ord = comparator(a[0], b[0]);
      if (ord !== 0) return ord;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const getIconColor = (added) => (added ? 'green' : 'red');

  useEffect(() => {
    if (selectedInvoiceStatus) {
      dispatch(getInvoiceRequest({ selectedGcnNo }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openInvoice, selectedGcnNo]);

  useEffect(() => {
    if (selectedExpenseStatus) {
      dispatch(getExpensesRequest({ selectedGcnNo }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openExpense, selectedGcnNo]);

  const filteredJobs = useMemo(() => {
    const list = data || [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((job) => jobMatchesQuickSearch(job, q));
  }, [data, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  return (
    <>
      <div className={classes.searchBar}>
        <div className={classes.searchField}>
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder='Search jobs…'
          />
        </div>
      </div>

      <TableContainer component={Paper} className={classes.tableScroller} data-tour='jobs-table-area'>
        <Table className={classes.table} aria-label='customized table'>
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <StyledTableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  padding={headCell.disablePadding ? 'none' : 'default'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'desc'}
                    onClick={createSortHandler(headCell.id)}
                    style={{ color: 'white' }}
                  >
                    {headCell.label}
                    {orderBy === headCell.id ? (
                      <span className={classes.visuallyHidden} style={{ color: 'black' }}>
                        {order === 'asc' ? 'd' : 'a'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(filteredJobs, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, rowIndexOnPage) => (
                <StyledTableRow
                  key={String(row._id != null ? row._id : rowPrimaryGcn(row))}
                  className={
                    highlightedJobNumber &&
                    String(highlightedJobNumber) === String(row.gcnno || row.carGcnno)
                      ? classes.highlightedRow
                      : ''
                  }
                >
                  <StyledTableCell align='center' width='200px'>
                    {row.gcnno ? row.gcnno : row.carGcnno }
                  </StyledTableCell>
                  <StyledTableCell align='center'>{row.consignorF}</StyledTableCell>
                  <StyledTableCell align='center'>
                    <IconButton
                      aria-label='view'
                      className={classes.actionBtn}
                      data-tour={page * rowsPerPage + rowIndexOnPage === 0 ? 'jobs-view-first' : undefined}
                      onClick={() => {
                        history.push(`/view-job/${rowPrimaryGcn(row)}`);
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </StyledTableCell>
                  <StyledTableCell align='center'>
                    <IconButton aria-label='edit' className={classes.actionBtn} onClick={() => onEditJobClick(rowPrimaryGcn(row))}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </StyledTableCell>
                  <StyledTableCell align='center'>
                    <IconButton aria-label='delete' className={classes.actionBtn} style={{ '&:hover': { color: '#ef4444' } }} onClick={() => handleClickOpen(rowPrimaryGcn(row))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </StyledTableCell>
                  <StyledTableCell align='center'>
                    <IconButton aria-label='download' className={classes.actionBtn} onClick={() => handleOpenDownload(rowPrimaryGcn(row))}>
                      <CloudDownloadIcon fontSize="small" />
                    </IconButton>
                  </StyledTableCell>
                  <StyledTableCell align='center'>
                    <IconButton
                      aria-label='invoice'
                      className={classes.actionBtn}
                      onClick={() => handleOpenInvoice(rowPrimaryGcn(row), row.isInvoiceAdded)}
                      style={{ color: getIconColor(row.isInvoiceAdded) }}
                    >
                      <MoneyIcon fontSize="small" />
                    </IconButton>
                  </StyledTableCell>

                  <StyledTableCell align='center'>
                    <IconButton
                      aria-label='expense'
                      className={classes.actionBtn}
                      onClick={() => handleOpenExpenses(rowPrimaryGcn(row), row.isExpenseAdded)}
                      style={{ color: getIconColor(row.isExpenseAdded) }}
                    >
                      <MoneyIcon fontSize="small" />
                    </IconButton>
                  </StyledTableCell>

                  <StyledTableCell align='center'>
                    <IconButton
                      aria-label='download_invoice'
                      className={classes.actionBtn}
                      disabled={!row.isInvoiceAdded}
                      onClick={() => handleOpenDownloadInvoice(rowPrimaryGcn(row))}
                    >
                      <CloudDownloadIcon fontSize="small" />
                    </IconButton>
                  </StyledTableCell>
                  <StyledTableCell align='center'>{row.date}</StyledTableCell>
                  <StyledTableCell align='center' style={{ minWidth: 220 }}>
                    <Box display='flex' alignItems='center' justifyContent='center' flexWrap='wrap'>
                      <FormControl variant='outlined' margin='dense' style={{ minWidth: 180 }}>
                        <Select
                          value={rowStageKey(row)}
                          onChange={(e) => {
                            onUpdateJobTracking({ trackingStage: e.target.value }, rowPrimaryGcn(row));
                          }}
                          displayEmpty
                        >
                          {TRACKING_STAGE_ORDERED_KEYS.map((key) => (
                            <MenuItem key={key} value={key}>
                              {TRACKING_STAGE_LABELS[key] || key}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        aria-label='tracking note'
                        size='small'
                        className={classes.actionBtn}
                        onClick={() =>
                          setNoteDialog({
                            open: true,
                            gcnno: rowPrimaryGcn(row),
                            draft: row.trackingNotes || '',
                          })
                        }
                      >
                        <NoteIcon fontSize='small' />
                      </IconButton>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell align='center'>{row.type}</StyledTableCell>
                  <StyledTableCell align='center'>{row.contact}</StyledTableCell>
                  <StyledTableCell align='center'>{row.email}</StyledTableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component='div'
        count={filteredJobs.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />

      <Dialog
        fullScreen={fullScreen}
        open={open}
        onClose={handleClose}
        aria-labelledby='responsive-dialog-title'
      >
        <DialogTitle id='responsive-dialog-title'>
          {'Are you sure, you want to detete this job?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>This will delete job permanently</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleDeleteClick} color='primary'>
            yes
          </Button>
          <Button onClick={handleClose} color='primary' autoFocus>
            cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noteDialog.open}
        onClose={() => setNoteDialog({ open: false, gcnno: null, draft: '' })}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Location / warehouse note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant='outlined'
            placeholder='Optional note shown to the customer on the tracking page'
            value={noteDialog.draft}
            onChange={(e) => setNoteDialog((n) => ({ ...n, draft: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog({ open: false, gcnno: null, draft: '' })} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (noteDialog.gcnno != null) {
                onUpdateJobTracking({ trackingNotes: noteDialog.draft }, noteDialog.gcnno);
              }
              setNoteDialog({ open: false, gcnno: null, draft: '' });
            }}
            color='primary'
            variant='contained'
          >
            Save note
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={openDownload}
        onClose={handleCloseDownload}
        aria-labelledby='responsive-dialog-title'
      >
        <DialogTitle id='responsive-dialog-title'>{'Converting to PDF'}</DialogTitle>
        <DialogContent>
          {/* <Invoice invoice={selectedJob} /> */}
          { selectedJob ?.gcnno && selectedJob ?.carGcnno ?
          <PDFDownloadLink
            document={<CreateJobPDF invoice={selectedJob} />}
            fileName={`${selectedJob ?.gcnno}_${selectedJob ?.carGcnno}_${selectedJob ?.consignorF}_${selectedJob ?.consignorL}_both_jobdocs.pdf`}
          >
            {({ blob, url, loading, error }) =>
              loading ? <HorizontalLoader /> : <PdfPrimaryDownloadButton>Download Now</PdfPrimaryDownloadButton>
            }
          </PDFDownloadLink> : 
          selectedJob ?.gcnno ?
           <PDFDownloadLink
             document={<CreateJobPDF invoice={selectedJob} />}
             fileName={`${selectedJob ?.gcnno}_${selectedJob ?.consignorF}_${selectedJob ?.consignorL}_household_jobdocs.pdf`}
           >
           {({ blob, url, loading, error }) =>
             loading ? <HorizontalLoader /> : <PdfPrimaryDownloadButton>Download Now</PdfPrimaryDownloadButton>
           }
         </PDFDownloadLink> :
         <PDFDownloadLink
          document={<CreateJobPDF invoice={selectedJob} />}
          fileName={`${selectedJob ?.carGcnno}_${selectedJob ?.consignorF}_${selectedJob ?.consignorL}_car_jobdocs.pdf`}
         >
         {({ blob, url, loading, error }) =>
           loading ? <HorizontalLoader /> : <PdfPrimaryDownloadButton>Download Now</PdfPrimaryDownloadButton>
         }
       </PDFDownloadLink>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDownload} color='primary' autoFocus>
            cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={openDownloadInvoice}
        onClose={handleCloseDownloadInvoice}
        aria-labelledby='responsive-dialog-title'
      >
        <DialogTitle id='responsive-dialog-title'>{'Converting to PDF'}</DialogTitle>
        <DialogContent>
          <PDFDownloadLink
            document={<CreateInvoiceOnlyPDF invoice={selectedInvoice} />}
            fileName={`${selectedInvoice ?.invoice[0]?.billno}_${selectedInvoice ?.consignorF}_${selectedInvoice ?.consignorL}_invoice.pdf`}
            >
            {({ blob, url, loading, error }) =>
              loading ? (
                <HorizontalLoader />
              ) : (
                <PdfPrimaryDownloadButton>Download Invoice Now</PdfPrimaryDownloadButton>
              )
            }
          </PDFDownloadLink>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDownloadInvoice} color='primary' autoFocus>
            cancel
          </Button>
        </DialogActions>
      </Dialog>

      {openInvoice && (
        <AddInvoiceDialog
          openInvoice={openInvoice}
          handleAddInvoice={handleAddInvoice}
          handleCancleInvoice={handleCancleInvoice}
          isEditing={selectedInvoiceStatus}
          gcnNo={selectedGcnNo}
        />
      )}

      {openExpense && (
        <AddExpenseDialog
          openExpense={openExpense}
          handleAddExpenses={handleAddExpenses}
          handleCancleExpenses={handleCancleExpenses}
          isEditing={selectedExpenseStatus}
        />
      )}
    </>
  );
};

export default AllJobsTable;
